import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAdminAuth, getAdminToken } from '../context/AdminAuthContext';
import RichTextEditor from '../components/RichTextEditor';
import { resolveImageUrl, formatPostDate } from '../lib/blogApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from '../components/ui/sonner';
import {
  Plus, Pencil, Trash2, LogOut, ExternalLink, Loader2, ImagePlus, ArrowLeft, Eye, FileText,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${getAdminToken()}` });

const emptyForm = {
  id: null,
  title: '',
  excerpt: '',
  author: 'The Vesta Team',
  tags: '',
  status: 'draft',
  cover_image: null,
  cover_file_id: null,
  body: '',
};

const AdminBlog = () => {
  const { user, logout } = useAdminAuth();
  const [view, setView] = useState('list'); // 'list' | 'editor'
  const [posts, setPosts] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const loadPosts = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await axios.get(`${API}/admin/blog/posts`, { headers: authHeaders() });
      setPosts(data);
    } catch (e) {
      toast.error('Failed to load posts');
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const startNew = () => {
    setForm(emptyForm);
    setView('editor');
  };

  const startEdit = async (postId) => {
    try {
      const { data } = await axios.get(`${API}/admin/blog/posts/${postId}`, { headers: authHeaders() });
      setForm({
        id: data.id,
        title: data.title || '',
        excerpt: data.excerpt || '',
        author: data.author || 'The Vesta Team',
        tags: (data.tags || []).join(', '),
        status: data.status || 'draft',
        cover_image: data.cover_image || null,
        cover_file_id: data.cover_file_id || null,
        body: data.body || '',
      });
      setView('editor');
    } catch (e) {
      toast.error('Failed to open post');
    }
  };

  const deletePost = async (postId, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/blog/posts/${postId}`, { headers: authHeaders() });
      toast.success('Post deleted');
      loadPosts();
    } catch (e) {
      toast.error('Failed to delete post');
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setForm((f) => ({ ...f, cover_image: data.url, cover_file_id: data.file_id }));
      toast.success('Cover image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Image upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const save = async (statusOverride) => {
    if (!form.title.trim()) {
      toast.error('Please add a title');
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      excerpt: form.excerpt,
      body: form.body,
      cover_image: form.cover_image,
      cover_file_id: form.cover_file_id,
      author: form.author || 'The Vesta Team',
      tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      status: statusOverride || form.status,
    };
    try {
      if (form.id) {
        await axios.put(`${API}/admin/blog/posts/${form.id}`, payload, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/admin/blog/posts`, payload, { headers: authHeaders() });
      }
      toast.success(payload.status === 'published' ? 'Post published' : 'Draft saved');
      setView('list');
      loadPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F2]">
      {/* Admin header */}
      <header className="bg-[#004D51] text-white">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-[#F39C49]" />
            <span className="font-black text-lg">Blog Manager</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-sm text-[#CFD2D4]">{user?.email}</span>
            <Link to="/blog" target="_blank" className="text-sm font-bold text-[#F39C49] hover:underline hidden sm:flex items-center">
              View blog <ExternalLink className="w-3.5 h-3.5 ml-1" />
            </Link>
            <Button onClick={logout} data-testid="admin-logout" className="bg-white/10 hover:bg-white/20 text-white font-bold h-9 px-4">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-10 max-w-5xl">
        {view === 'list' && (
          <div data-testid="admin-blog-list">
            <div className="flex items-center justify-between mb-8">
              <h1 className="text-3xl font-black text-[#004D51]">Community Posts</h1>
              <Button onClick={startNew} data-testid="new-post-btn" className="bg-[#DB5A14] hover:bg-[#F39C49] text-white font-black">
                <Plus className="w-5 h-5 mr-2" /> New Post
              </Button>
            </div>

            {loadingList ? (
              <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#004D51] animate-spin" /></div>
            ) : posts.length === 0 ? (
              <div className="bg-white rounded-xl border-2 border-dashed border-[#004D51]/30 p-12 text-center">
                <FileText className="w-12 h-12 text-[#004D51]/40 mx-auto mb-4" />
                <p className="text-[#545759] mb-6">No posts yet. Create your first community post!</p>
                <Button onClick={startNew} className="bg-[#DB5A14] hover:bg-[#F39C49] text-white font-bold">
                  <Plus className="w-4 h-4 mr-2" /> New Post
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {posts.map((post) => (
                  <div
                    key={post.id}
                    data-testid={`admin-post-${post.id}`}
                    className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4 hover:shadow-md transition-shadow"
                  >
                    <div className="w-16 h-16 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                      {post.cover_image ? (
                        <img src={resolveImageUrl(post.cover_image)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#004D51]/30"><ImagePlus className="w-6 h-6" /></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${post.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {post.status === 'published' ? 'Published' : 'Draft'}
                        </span>
                        <span className="text-xs text-[#545759]">{formatPostDate(post.published_at || post.created_at)}</span>
                      </div>
                      <h3 className="font-black text-[#004D51] truncate">{post.title}</h3>
                      <p className="text-sm text-[#545759] truncate">{post.excerpt}</p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {post.status === 'published' && (
                        <a href={`/blog/${post.slug}`} target="_blank" rel="noreferrer" title="View" className="w-9 h-9 flex items-center justify-center rounded-lg text-[#004D51] hover:bg-[#F3CEB2]/40">
                          <Eye className="w-4 h-4" />
                        </a>
                      )}
                      <button onClick={() => startEdit(post.id)} data-testid={`edit-post-${post.id}`} title="Edit" className="w-9 h-9 flex items-center justify-center rounded-lg text-[#004D51] hover:bg-[#F3CEB2]/40">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => deletePost(post.id, post.title)} data-testid={`delete-post-${post.id}`} title="Delete" className="w-9 h-9 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {view === 'editor' && (
          <div data-testid="admin-blog-editor">
            <button onClick={() => setView('list')} className="inline-flex items-center text-[#DB5A14] hover:text-[#F39C49] font-bold mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to posts
            </button>
            <h1 className="text-3xl font-black text-[#004D51] mb-8">{form.id ? 'Edit Post' : 'New Post'}</h1>

            <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
              <div>
                <label className="block text-sm font-black text-[#004D51] mb-1">Title</label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Post title"
                  data-testid="post-title-input"
                  className="h-12 border-2 border-[#004D51]/30 focus:border-[#004D51] text-lg font-bold"
                />
              </div>

              <div>
                <label className="block text-sm font-black text-[#004D51] mb-1">Excerpt / summary</label>
                <textarea
                  value={form.excerpt}
                  onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                  placeholder="A short summary shown on the blog list"
                  data-testid="post-excerpt-input"
                  rows={2}
                  className="w-full rounded-lg border-2 border-[#004D51]/30 focus:border-[#004D51] focus:outline-none px-3 py-2 text-[#2b2b2b]"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-black text-[#004D51] mb-1">Author</label>
                  <Input
                    value={form.author}
                    onChange={(e) => setForm({ ...form, author: e.target.value })}
                    data-testid="post-author-input"
                    className="h-11 border-2 border-[#004D51]/30 focus:border-[#004D51]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-black text-[#004D51] mb-1">Tags (comma separated)</label>
                  <Input
                    value={form.tags}
                    onChange={(e) => setForm({ ...form, tags: e.target.value })}
                    placeholder="Career, Community, Gear Guide"
                    data-testid="post-tags-input"
                    className="h-11 border-2 border-[#004D51]/30 focus:border-[#004D51]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-black text-[#004D51] mb-1">Cover image</label>
                <div className="flex items-center gap-4">
                  <div className="w-32 h-20 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0 border border-gray-200">
                    {form.cover_image ? (
                      <img src={resolveImageUrl(form.cover_image)} alt="cover" className="w-full h-full object-cover" data-testid="cover-preview" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#004D51]/30"><ImagePlus className="w-6 h-6" /></div>
                    )}
                  </div>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleCoverUpload} className="hidden" data-testid="cover-file-input" id="cover-file-input" />
                  <Button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="upload-cover-btn"
                    className="bg-[#004D51] hover:bg-[#003338] text-white font-bold"
                  >
                    {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><ImagePlus className="w-4 h-4 mr-2" /> Upload image</>}
                  </Button>
                  {form.cover_image && (
                    <button type="button" onClick={() => setForm({ ...form, cover_image: null, cover_file_id: null })} className="text-sm text-red-500 font-bold hover:underline">
                      Remove
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-black text-[#004D51] mb-1">Body</label>
                <RichTextEditor value={form.body} onChange={(html) => setForm((f) => ({ ...f, body: html }))} />
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-bold text-[#004D51]">Status:</span>
                  <span className={`font-bold ${form.status === 'published' ? 'text-green-600' : 'text-amber-600'}`}>
                    {form.status === 'published' ? 'Published' : 'Draft'}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    onClick={() => save('draft')}
                    disabled={saving}
                    data-testid="save-draft-btn"
                    className="bg-white border-2 border-[#004D51] text-[#004D51] hover:bg-[#F3CEB2]/30 font-bold"
                  >
                    Save as Draft
                  </Button>
                  <Button
                    onClick={() => save('published')}
                    disabled={saving}
                    data-testid="publish-btn"
                    className="bg-[#DB5A14] hover:bg-[#F39C49] text-white font-black"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Publish
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminBlog;
